import UserIcon from '@mui/icons-material/Person';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import es from 'dayjs/locale/es-mx';
import useFetch from 'http-react';
import React from 'react';
import { Calendar, Views, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import * as Redux from 'react-redux';
import LoadingComponent from '../../../components/Loading';
import Modal from '../../../components/modals/Modal';
import { MORADO_COLOR } from '../../../constants/ColorsConst';
import { getDateTimezone } from '../../../helpers';
import { useNotification } from '../../../helpers/notification';
import { deleteReservasService } from '../../../services';
import { baseApi, token } from '../../../services/Apis';

// Configuración de dayjs
dayjs.locale(es);
var _timezone = require('dayjs/plugin/timezone');
dayjs.extend(_timezone);

// Configuración del localizador para react-big-calendar
const localizer = dayjsLocalizer(dayjs);

// Mensajes personalizados para el calendario
export const messagesCalendar = {
  day: 'Día',
  week: 'Semana',
  agenda: 'Diario',
  month: 'Mes',
};

export default function CalendarReservas({
  areaIds = [],
  dates = [],
  refresh = false,
  interactive = false,
  allDates = false,
  onlyReservations = false,
  userCodeId = null,
  setIsCalendar,
}) {
  const entidadesStore = Redux.useSelector((state) => state.entidades);
  const [date, setDate] = React.useState(
    allDates ? dayjs().startOf('day').toDate() : dates[0] || null
  );
  const [view, setView] = React.useState(Views.MONTH);
  const [areaId, setAreaId] = React.useState(null);
  const [eventos, setEventos] = React.useState([]);

  const { timezone } = entidadesStore.activo.country;
  const { addNotification } = useNotification();

  const [viewModalBlock, setViewModalBlock] = React.useState(null);
  const [selectedEvent, setSelectedEvent] = React.useState(null);

  React.useEffect(() => {
    if (areaIds.length > 0) {
      setAreaId(areaIds[0]);
    }
  }, [areaIds, date]);

  const {
    data: subareas,
    reFetch,
    loading: loadingSubareas,
  } = useFetch(`${baseApi}/reservations/by-area`, {
    headers: token({}),
    query: {
      areaId: areaIds.length > 0 ? areaIds[0] : undefined,
      date: date
        ? getDateTimezone(dayjs(date).format('YYYY-MM-DD HH:mm:ss'), timezone)
        : undefined,
      userCodeId: userCodeId || undefined,
    },
    onResolve: (data) => {
      console.log('Datos de reservas recibidos correctamente');
    },
    onError: (error) => {
      console.error('Error al obtener reservas:', error);
      addNotification('Error al cargar las reservas', { warning: true });
    },
    enabled: !!areaIds.length && !!date,
  });

  const { data: areaData, loading: loadingAreas } = useFetch(
    `${baseApi}/areas/short`,
    {
      headers: token({}),
      query: {
        entityId: entidadesStore.activo.id,
      },
    }
  );

  const areas = areaData?.filter((el) => areaIds.includes(el.id)) || [];

  const onSelectBlock = (block, subarea) => {
    setViewModalBlock({ block, subarea });
  };

  React.useEffect(() => {
    if (refresh) {
      reFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  React.useEffect(() => {
    if (dates.length > 0) {
      setDate(dates[0]);
    }
  }, [dates]);

  // Transformar los datos de subareas a eventos para react-big-calendar
  React.useEffect(() => {
    if (subareas && subareas.length > 0) {
      const events = [];
      subareas.forEach((subarea) => {
        if (!subarea.blocks || !Array.isArray(subarea.blocks)) {
          console.warn('Subarea sin bloques válidos:', subarea);
          return;
        }

        subarea.blocks.forEach((block) => {
          if (!block) {
            console.warn('Bloque no válido en subarea:', subarea.name);
            return;
          }

          try {
            // Solo crear eventos para los bloques reservados
            if (block.reservation) {
              const startTime = dayjs(block.entryTime);
              const endTime = block.exitTime
                ? dayjs(block.exitTime)
                : startTime.add(1, 'hour');

              let isShared = false;

              if (
                block.reservation.userCode &&
                subarea.maxPersonsShared > 1 &&
                subarea.maxPersonsShared - block.reservation.personsShared > 0
              ) {
                isShared = true;
              }

              // Establecer el título como "Reservado" para todos los eventos
              const title = 'Reservado';

              events.push({
                id: `${subarea.id}-${block.id}`,
                title,
                start: startTime.toDate(),
                end: endTime.toDate(),
                block,
                subarea,
                isShared,
                reservation: block.reservation,
                color: isShared
                  ? '#9C27B0' // morado para compartido
                  : block.reservation.type === 'block'
                  ? '#2b2b2b' // gris oscuro para bloqueos
                  : '#F44336', // rojo para reservado
              });
            }
          } catch (error) {
            console.error('Error al procesar bloque:', error, block);
          }
        });
      });

      console.log('Eventos procesados:', events.length);
      setEventos(events);
    } else {
      setEventos([]);
    }
  }, [subareas, date]);

  const handleDeleteReservation = async (single = true) => {
    try {
      await deleteReservasService(viewModalBlock.block.reservation.id, {
        single,
      });
      setViewModalBlock(null);
      reFetch();
      addNotification('Reserva eliminada');
    } catch (error) {
      addNotification(
        error.response?.data?.message ?? 'Error al eliminar la reserva',
        {
          warning: true,
        }
      );
    }
  };

  // Componente personalizado para los eventos en el calendario
  const EventComponent = (eventProps) => {
    const { event } = eventProps;
    const { block } = event;
    const isMonth = view === Views.MONTH;

    // Personaliza el estilo de cada evento
    const eventStyle = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      height: '100%',
      padding: '2px',
    };

    const styleText = {
      fontSize: isMonth ? '10pt' : '12pt',
      fontWeight: 'bold',
    };

    return (
      <Box sx={eventStyle}>
        <Stack direction="row" spacing={1} alignItems="center">
          <UserIcon fontSize="inherit" sx={{ color: 'white' }} />
          <Typography textAlign="left" sx={styleText}>
            {block.reservation.type === 'reservation' ? block.reservation.userCode?.code?.code || 'Reservado' : block.reservation.event ? 'Evento' : 'Bloqueado'}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const handleRangeChange = async (dateRange) => {
    console.log('Cambio de rango:', dateRange);
    if (dateRange.start) {
      setDate(dateRange.start);
    } else if (Array.isArray(dateRange) && dateRange.length > 0) {
      setDate(dateRange[0]);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setViewModalBlock(null);
  };

  // Determinar si hay un modal abierto y qué datos mostrar
  const modalData = selectedEvent || viewModalBlock;
  const isModalOpen = !!selectedEvent || !!viewModalBlock;
  const isManageMode = !!viewModalBlock;

  return (
    <Grid container sx={{ paddingTop: 2 }}>
      <Grid
        item
        xs={12}
        md={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: 20 }}>
            Tablero de reservas
          </Typography>
          <Stack direction="row" spacing={2}>
            {areaIds.length > 1 && (
              <FormControl sx={{ minWidth: '250px' }}>
                <InputLabel id="area">Área</InputLabel>
                <Select
                  size="small"
                  value={areaId}
                  label="Área"
                  onChange={(e) => {
                    setAreaId(e.target.value);
                    const area = areas.find(a => a.id === e.target.value);
                    if (area?.blockReservation === 'day') {
                      setIsCalendar(true);
                    } else {
                      setIsCalendar(false);
                    }
                  }}
                >
                  {areas.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      {area.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(allDates || dates.length > 1) && (
              <FormControl sx={{ minWidth: '250px' }}>
                <LocalizationProvider
                  adapterLocale={'es'}
                  dateAdapter={AdapterDayjs}
                >
                  <DatePicker
                    label={'Fecha'}
                    value={date}
                    openTo="day"
                    size="small"
                    adapterLocale={'ve'}
                    shouldDisableDate={(date) =>
                      allDates
                        ? undefined
                        : !dates.find((el) => dayjs(el).isSame(date, 'day'))
                    }
                    onChange={(date) => {
                      setDate(date);
                    }}
                    views={['day']}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" required {...params} />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </Grid>

      <Grid item xs={12} md={12}>
        <LoadingComponent
          isLoading={loadingAreas || loadingSubareas}
          text="Cargando bloques de horas..."
        />
      </Grid>

      {/* <pre>{JSON.stringify({dates, date, eventos}, null, 2)}</pre> */}

      <Grid item xs={12} md={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          {/* {eventos.length > 0 ? (
          ) : (
            <Typography variant="h6" sx={{ textAlign: 'center', padding: 2 }}>
              {loadingSubareas
                ? 'Cargando...'
                : 'No hay reservas para esta fecha'}
            </Typography>
          )} */}
          
          <Calendar
              messages={messagesCalendar}
              localizer={localizer}
              events={eventos}
              startAccessor="start"
              endAccessor="end"
              views={['month']}
              defaultView={Views.MONTH}
              view={view}
              onView={(newView) => setView(newView)}
              defaultDate={date || new Date()}
              date={date}
              onNavigate={(newDate) => {
                setDate(newDate);
              }}
              style={{ height: '75vh' }}
              components={{
                event: EventComponent,
                toolbar: () => (
                  <div style={{ 
                    padding: '10px', 
                    textAlign: 'center', 
                    fontSize: '1.2em', 
                    fontWeight: 'bold' 
                  }}>
                    {dayjs(date).format('MMMM YYYY')}
                  </div>
                )
              }}
              eventPropGetter={(event) => {
                const style = {
                  backgroundColor: event.color,
                  color: 'white',
                  borderRadius: '7px',
                  border: '1px solid white',
                  fontSize: '10pt',
                };

                return {
                  className: '',
                  style,
                };
              }}
              onSelectEvent={handleSelectEvent}
              selectable={false}
              popup
              onRangeChange={handleRangeChange}
              navigation={false}
            />
        </Paper>
      </Grid>

      {modalData && (
        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          title={
            <div>
              {`Detalles de la Reserva - ${dayjs(modalData.start).format(
                'DD/MM/YYYY'
              )}`}
              <Chip
                label={
                  (isManageMode
                    ? modalData.block.reservation
                    : modalData.reservation
                  ).event
                    ? 'Evento'
                    : (isManageMode
                        ? modalData.block.reservation
                        : modalData.reservation
                      ).type === 'block'
                    ? 'Bloqueo'
                    : 'Reserva'
                }
                style={{
                  color: '#fff',
                  backgroundColor: (isManageMode
                    ? modalData.block.reservation
                    : modalData.reservation
                  ).event
                    ? '#FF9800'
                    : (isManageMode
                        ? modalData.block.reservation
                        : modalData.reservation
                      ).type === 'block'
                    ? '#F44336'
                    : '#4CAF50',
                  marginLeft: '10px',
                }}
              />
            </div>
          }
        >
          <DialogContent>
            {isManageMode ? (
              modalData.block.reservation.type === 'reservation' && (
                <>
                  <Typography variant="h6" color="initial" sx={{ mb: 3 }}>
                    Seguro que deseas eliminar la reserva?
                  </Typography>
                  <Typography variant="body1" color="initial">
                    Nombre:{' '}
                    {
                      modalData.block.reservation.userCode.user.profile
                        .firstName
                    }{' '}
                    {modalData.block.reservation.userCode.user.profile.lastName}
                  </Typography>
                  <Typography variant="body1" color="initial">
                    Código: {modalData.block.reservation.userCode.code.code}
                  </Typography>

                  {modalData.block.reservation.payment && (
                    <Typography variant="body1" color="initial">
                      Total: ${modalData.block.reservation.payment.amount}
                    </Typography>
                  )}
                </>
              )
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {modalData.subarea.name}
                  </Typography>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Horario:
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(modalData.block.entryTime).format('hh:mm A')} -
                    {modalData.block.exitTime
                      ? dayjs(modalData.block.exitTime).format(' hh:mm A')
                      : dayjs(modalData.block.entryTime)
                          .add(1, 'hour')
                          .format(' hh:mm A')}
                  </Typography>
                </Grid>

                {modalData.reservation.type === 'block' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Descripción:
                    </Typography>
                    <Typography variant="body1">
                      {modalData.reservation.description || '-'}
                    </Typography>
                  </Grid>
                )}

                {modalData.reservation.type === 'reservation' && (
                  <>
                    {modalData.reservation.userCode && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 'bold' }}
                          >
                            Código:
                          </Typography>
                          <Typography variant="body1">
                            {modalData.reservation.userCode.code.code}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 'bold' }}
                          >
                            Usuario:
                          </Typography>
                          <Typography variant="body1">
                            {
                              modalData.reservation.userCode.user.profile
                                .firstName
                            }{' '}
                            {
                              modalData.reservation.userCode.user.profile
                                .lastName
                            }
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 'bold' }}
                          >
                            Email:
                          </Typography>
                          <Typography variant="body1">
                            {modalData.reservation.userCode.user.email}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {modalData.subarea.maxPersonsShared > 1 && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold' }}
                        >
                          Estado:
                        </Typography>
                        <Typography variant="body1">
                          {modalData.subarea.maxPersonsShared -
                            modalData.reservation.personsShared >
                          0
                            ? `Compartido (${
                                modalData.subarea.maxPersonsShared -
                                modalData.reservation.personsShared
                              } lugares disponibles)`
                            : 'Completo'}
                        </Typography>
                      </Grid>
                    )}

                    {modalData.reservation.payment && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold' }}
                        >
                          Pago:
                        </Typography>
                        <Typography variant="body1">
                          ${modalData.reservation.payment.amount}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}
          </DialogContent>

          <DialogActions sx={{ padding: 2 }}>
            <Button sx={{ color: MORADO_COLOR }} onClick={handleCloseModal}>
              {isManageMode ? 'CANCELAR' : 'CERRAR'}
            </Button>

            {isManageMode && (
              <>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={async () => {
                    handleDeleteReservation();
                  }}
                >
                  ELIMINAR
                </Button>

                <Button
                  color="error"
                  variant="outlined"
                  onClick={async () => {
                    handleDeleteReservation(false);
                  }}
                >
                  ELIMINAR BLOQUES
                </Button>
              </>
            )}

            {interactive &&
              !isManageMode &&
              modalData.reservation.type !== 'block' && (
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    setViewModalBlock({
                      block: modalData.block,
                      subarea: modalData.subarea,
                    });
                    setSelectedEvent(null);
                  }}
                >
                  GESTIONAR RESERVA
                </Button>
              )}
          </DialogActions>
        </Modal>
      )}
    </Grid>
  );
}
